import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ArrowRight, BookOpen, Loader2, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  CADENCE_DAY_OPTIONS,
  ENTITY_OPTIONS,
  SERVICE_FAMILIES,
  SERVICE_LOOKUP,
  cadenceSentence,
  type AffirmationSigner,
  type CadenceType,
  type CovenantWorkflowData,
  type EntityType,
  type SelectedService,
} from './covenantContent';

interface CovenantPhase1Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

const STEP_TITLES = [
  'Start here',
  'Which entity is adopting',
  'Who signs',
  'Services',
  'Exhibit A, the schedule',
  'Review',
];

function emptyService(id: string): SelectedService {
  const meta = SERVICE_LOOKUP[id];
  return {
    id,
    family: meta?.family || '',
    label: meta?.label || id,
    description: '',
    performed_by: '',
    fee_basis: '',
    fee_amount: '',
    reasonableness: '',
    stranger_test: '',
  };
}

export function CovenantPhase1({ task, onPhaseComplete }: CovenantPhase1Props) {
  const { toast } = useToast();
  const existing: CovenantWorkflowData = task?.workflow_data?.covenant || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [unaName, setUnaName] = useState<string>('');

  const [entityType, setEntityType] = useState<EntityType | undefined>(existing.entity_type);
  const [entityLegalName, setEntityLegalName] = useState(existing.entity_legal_name || '');
  const [signerName, setSignerName] = useState(existing.signer_name || '');
  const [signerCapacity, setSignerCapacity] = useState(existing.signer_capacity || '');
  const [officerName, setOfficerName] = useState(existing.association_officer_name || '');
  const [affirmationSigners, setAffirmationSigners] = useState<AffirmationSigner[]>(
    existing.affirmation_signers?.length
      ? existing.affirmation_signers
      : [{ id: `s-${Date.now()}`, name: '', capacity: '' }],
  );
  const [contributionOnly, setContributionOnly] = useState(!!existing.contribution_only);
  const [services, setServices] = useState<SelectedService[]>(existing.services || []);
  const [cadenceType, setCadenceType] = useState<CadenceType>(existing.cadence_type || 'fixed_day');
  const [cadenceDay, setCadenceDay] = useState<'1' | '15' | 'month_end'>(existing.cadence_day || '15');
  const [cadencePercentage, setCadencePercentage] = useState(existing.cadence_percentage || '');
  const [initialTransition, setInitialTransition] = useState(existing.initial_transition || '');
  const [covenantDate, setCovenantDate] = useState(existing.covenant_date || '');

  useEffect(() => {
    async function loadUna() {
      if (!task?.una_id) return;
      const { data } = await supabase
        .from('unas')
        .select('name, primary_officer_role, treasury_secretary_id')
        .eq('id', task.una_id)
        .maybeSingle();
      if (data) {
        setUnaName(data.name || '');
        if (!officerName && data.treasury_secretary_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', data.treasury_secretary_id)
            .maybeSingle();
          if (profile?.full_name) setOfficerName(profile.full_name);
        }
      }
    }
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.una_id]);

  const data: CovenantWorkflowData = useMemo(
    () => ({
      entity_type: entityType,
      entity_legal_name: entityLegalName,
      signer_name: signerName,
      signer_capacity: signerCapacity,
      association_officer_name: officerName,
      affirmation_signers: affirmationSigners.filter((s) => s.name.trim()),
      services: contributionOnly ? [] : services,
      contribution_only: contributionOnly,
      cadence_type: cadenceType,
      cadence_day: cadenceDay,
      cadence_percentage: cadencePercentage,
      initial_transition: initialTransition,
      covenant_date: covenantDate,
    }),
    [
      entityType,
      entityLegalName,
      signerName,
      signerCapacity,
      officerName,
      affirmationSigners,
      services,
      contributionOnly,
      cadenceType,
      cadenceDay,
      cadencePercentage,
      initialTransition,
      covenantDate,
    ],
  );

  async function persist() {
    setSaving(true);
    try {
      const nextData = { ...(task.workflow_data || {}), covenant: data };
      await supabase.from('task_workflows').update({ workflow_data: nextData }).eq('task_id', task.id);
      
      if (task.workflow_data) task.workflow_data.covenant = data;
    } catch (e) {
      console.warn('Covenant save non fatal error', e);
    } finally {
      setSaving(false);
    }
  }

  function toggleService(id: string, checked: boolean) {
    setServices((prev) => (checked ? [...prev, emptyService(id)] : prev.filter((s) => s.id !== id)));
  }

  function updateService(id: string, field: keyof SelectedService, value: string) {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  }

  function stepIsValid(current: number): string | null {
    if (current === 2 && !entityType) return 'Choose which kind of entity is adopting.';
    if (current === 3) {
      if (!entityLegalName.trim()) return 'Enter the exact legal name of the entity.';
      if (!signerName.trim() || !signerCapacity.trim()) return 'Enter the signer name and the capacity they sign in.';
      if (!officerName.trim()) return 'Enter the name of the Association Officer who signs.';
    }
    if (current === 4 && !contributionOnly) {
      for (const s of services) {
        if (!s.description.trim()) return `Say what is actually done for "${s.label}".`;
        if (!s.performed_by.trim()) return `Say who performs "${s.label}".`;
        if (!s.fee_basis.trim() || !s.fee_amount.trim()) return `Give the fee basis and amount for "${s.label}".`;
        if (!s.reasonableness.trim()) return `Write one sentence on why the fee for "${s.label}" is reasonable.`;
        if (!s.stranger_test.trim()) return `Finish the stranger sentence for "${s.label}".`;
      }
    }
    if (current === 5) {
      if (cadenceType === 'percentage' && !cadencePercentage.trim()) return 'Enter the share that settles at month end.';
      if (!initialTransition.trim()) return 'Say what moves in the first transition.';
    }
    return null;
  }

  async function goNext() {
    const problem = stepIsValid(step);
    if (problem) {
      toast({ title: 'One thing first', description: problem, variant: 'destructive' });
      return;
    }
    await persist();
    if (step === 4 && contributionOnly) {
      setStep(5);
      return;
    }
    setStep((s) => Math.min(6, s + 1));
  }

  async function finish() {
    await persist();
    toast({ title: 'Saved', description: 'Your answers are saved. Next you prepare the papers.' });
    onPhaseComplete();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">
              Step {step} of 6: {STEP_TITLES[step - 1]}
            </CardTitle>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <h4 className="font-medium mb-1">What you need to know</h4>
                <p>
                  This workflow helps you get ready to sign the Religious Covenant. It does not write the covenant.
                  Your covenant comes from your packet, and it was reviewed by counsel. What you build here is the
                  paperwork that goes around it. An entity that holds resources in stewardship needs someone to
                  actually provide that stewardship. Someone keeps its accounts. Someone orders its governance. Someone
                  cares for what it holds. Someone forms its people. Someone gathers its community. An entity that does
                  not have those people in house gets them from the organization set up to provide them. That is the
                  whole reason this covenant exists. The affirmations only mean something if they are true. So everyone
                  who signs reads the covenant first, all the way through, before anybody picks up a pen.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">What you need to do</h4>
                <p>
                  Choose the entity, name the people, pick the real services, and set the giving schedule. The portal
                  prepares the papers, and everyone who signs reads the covenant first.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Your schedule</h4>
                <p>
                  Transfers follow the written schedule you set, never on demand. The portal reminds you when anything
                  needs your attention and keeps the record.
                </p>
              </div>
              <Button asChild variant="outline" size="sm">
                <a href="/support/library/executing-religious-covenant">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Read: Executing the Religious Covenant
                </a>
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This choice sets the form of the authorization paper. Each kind of entity says yes in its own voice.
              </p>
              <RadioGroup value={entityType} onValueChange={(v) => setEntityType(v as EntityType)} className="space-y-3">
                {ENTITY_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value={opt.value} id={`entity-${opt.value}`} className="mt-1" />
                    <Label htmlFor={`entity-${opt.value}`} className="font-normal cursor-pointer">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-sm text-muted-foreground">{opt.helper}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <Button asChild variant="link" size="sm" className="px-0">
                <a href="/support/library/entity-authorization-resolutions">
                  How the authorization formats differ
                </a>
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="entity-name">Exact legal name of the entity</Label>
                  <Input
                    id="entity-name"
                    value={entityLegalName}
                    onChange={(e) => setEntityLegalName(e.target.value)}
                    placeholder="As it appears on the filing papers"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="covenant-date">Date you plan to sign</Label>
                  <Input
                    id="covenant-date"
                    type="date"
                    value={covenantDate}
                    onChange={(e) => setCovenantDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signer-name">Who signs for the entity</Label>
                  <Input id="signer-name" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signer-capacity">In what capacity</Label>
                  <Input
                    id="signer-capacity"
                    value={signerCapacity}
                    onChange={(e) => setSignerCapacity(e.target.value)}
                    placeholder="Manager, President, and so on"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="officer-name">
                    Association Officer who signs{unaName ? ` for ${unaName}` : ''}
                  </Label>
                  <Input id="officer-name" value={officerName} onChange={(e) => setOfficerName(e.target.value)} />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>People who sign an affirmation page</Label>
                    <p className="text-sm text-muted-foreground">
                      One page each, signed in their own name, not for the entity.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAffirmationSigners((prev) => [...prev, { id: `s-${Date.now()}`, name: '', capacity: '' }])
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add person
                  </Button>
                </div>
                {affirmationSigners.map((s, idx) => (
                  <div key={s.id} className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      value={s.name}
                      placeholder={`Full name of person ${idx + 1}`}
                      onChange={(e) =>
                        setAffirmationSigners((prev) =>
                          prev.map((p) => (p.id === s.id ? { ...p, name: e.target.value } : p)),
                        )
                      }
                    />
                    <Input
                      value={s.capacity}
                      placeholder="How they are involved, optional"
                      onChange={(e) =>
                        setAffirmationSigners((prev) =>
                          prev.map((p) => (p.id === s.id ? { ...p, capacity: e.target.value } : p)),
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setAffirmationSigners((prev) => prev.filter((p) => p.id !== s.id))}
                      aria-label="Remove person"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <Alert>
                <AlertDescription className="text-sm">
                  Pick only the services that are really going to be rendered. A service that is not actually done does
                  not belong on this list.
                </AlertDescription>
              </Alert>

              <div className="flex items-start gap-3 rounded-lg border p-3">
                <Checkbox
                  id="contribution-only"
                  checked={contributionOnly}
                  onCheckedChange={(c) => setContributionOnly(!!c)}
                  className="mt-1"
                />
                <Label htmlFor="contribution-only" className="font-normal cursor-pointer">
                  <span className="font-medium block">No services for now</span>
                  <span className="text-sm text-muted-foreground">
                    This is a contribution only covenant. You can skip ahead to the schedule.
                  </span>
                </Label>
              </div>

              {!contributionOnly &&
                SERVICE_FAMILIES.map((fam) => (
                  <div key={fam.key} className="space-y-3">
                    <div>
                      <h4 className="font-medium">{fam.label}</h4>
                      <p className="text-sm text-muted-foreground">{fam.blurb}</p>
                    </div>
                    {fam.services.map((svc) => {
                      const chosen = services.find((s) => s.id === svc.id);
                      return (
                        <div key={svc.id} className="rounded-lg border p-3 space-y-3">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`svc-${svc.id}`}
                              checked={!!chosen}
                              onCheckedChange={(c) => toggleService(svc.id, !!c)}
                              className="mt-1"
                            />
                            <Label htmlFor={`svc-${svc.id}`} className="font-normal cursor-pointer">
                              <span className="font-medium block">{svc.label}</span>
                              <span className="text-sm text-muted-foreground">{svc.hint}</span>
                            </Label>
                          </div>
                          {chosen && (
                            <div className="grid gap-3 pl-7">
                              <div className="space-y-1">
                                <Label className="text-sm">What is actually done, for whom, how often</Label>
                                <Textarea
                                  rows={2}
                                  value={chosen.description}
                                  onChange={(e) => updateService(svc.id, 'description', e.target.value)}
                                />
                              </div>
                              <div className="grid gap-3 sm:grid-cols-3">
                                <div className="space-y-1">
                                  <Label className="text-sm">Who performs it</Label>
                                  <Input
                                    value={chosen.performed_by}
                                    onChange={(e) => updateService(svc.id, 'performed_by', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-sm">Fee basis</Label>
                                  <Input
                                    value={chosen.fee_basis}
                                    placeholder="Monthly retainer, per engagement, share"
                                    onChange={(e) => updateService(svc.id, 'fee_basis', e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-sm">Amount</Label>
                                  <Input
                                    value={chosen.fee_amount}
                                    onChange={(e) => updateService(svc.id, 'fee_amount', e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-sm">One sentence on why this fee is reasonable</Label>
                                <Input
                                  value={chosen.reasonableness}
                                  onChange={(e) => updateService(svc.id, 'reasonableness', e.target.value)}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-sm">
                                  Read by a stranger, this agreement would look like:
                                </Label>
                                <Input
                                  value={chosen.stranger_test}
                                  onChange={(e) => updateService(svc.id, 'stranger_test', e.target.value)}
                                  placeholder="Finish the sentence honestly"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Label>When transfers happen</Label>
                <RadioGroup value={cadenceType} onValueChange={(v) => setCadenceType(v as CadenceType)} className="space-y-3">
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="fixed_day" id="cadence-fixed" className="mt-1" />
                    <Label htmlFor="cadence-fixed" className="font-normal cursor-pointer">
                      <span className="font-medium block">A fixed day each month</span>
                      <span className="text-sm text-muted-foreground">Same day, every month, no thinking required.</span>
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value="percentage" id="cadence-pct" className="mt-1" />
                    <Label htmlFor="cadence-pct" className="font-normal cursor-pointer">
                      <span className="font-medium block">A stated share, settled at month end</span>
                      <span className="text-sm text-muted-foreground">
                        A set percentage, worked out and moved at the end of the month.
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {cadenceType === 'fixed_day' ? (
                <div className="space-y-2">
                  <Label>Which day</Label>
                  <RadioGroup
                    value={cadenceDay}
                    onValueChange={(v) => setCadenceDay(v as '1' | '15' | 'month_end')}
                    className="space-y-2"
                  >
                    {CADENCE_DAY_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center gap-3">
                        <RadioGroupItem value={opt.value} id={`day-${opt.value}`} />
                        <Label htmlFor={`day-${opt.value}`} className="font-normal cursor-pointer">
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ) : (
                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="pct">Share, as a percentage</Label>
                  <Input id="pct" value={cadencePercentage} onChange={(e) => setCadencePercentage(e.target.value)} />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="initial-transition">What moves in the first transition</Label>
                <Textarea
                  id="initial-transition"
                  rows={3}
                  value={initialTransition}
                  onChange={(e) => setInitialTransition(e.target.value)}
                  placeholder="Name what moves first and roughly when"
                />
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5 text-sm">
              <Alert>
                <AlertDescription>
                  The covenant itself comes from your packet. It is signed in the same sitting as everything below.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <h4 className="font-medium">Entity</h4>
                <p>{ENTITY_OPTIONS.find((o) => o.value === entityType)?.label || 'Not chosen yet'}</p>
                <p className="text-muted-foreground">{entityLegalName || 'No legal name yet'}</p>
                <Button variant="link" size="sm" className="px-0" onClick={() => setStep(2)}>
                  Change this
                </Button>
              </div>

              <div className="space-y-1">
                <h4 className="font-medium">Who signs</h4>
                <p>
                  {signerName || '[signer]'} as {signerCapacity || '[capacity]'} for the entity.
                </p>
                <p>{officerName || '[Officer]'} for the Association.</p>
                <p className="text-muted-foreground">
                  Affirmation pages: {(affirmationSigners.filter((s) => s.name.trim()) || []).map((s) => s.name).join(', ') || 'none yet'}
                </p>
                <Button variant="link" size="sm" className="px-0" onClick={() => setStep(3)}>
                  Change this
                </Button>
              </div>

              <div className="space-y-1">
                <h4 className="font-medium">Services</h4>
                {contributionOnly || services.length === 0 ? (
                  <p>No services. This is a contribution only covenant.</p>
                ) : (
                  <ul className="list-disc pl-5 space-y-1">
                    {services.map((s) => (
                      <li key={s.id}>
                        <span className="font-medium">{s.label}</span>: {s.description} ({s.fee_basis} {s.fee_amount})
                      </li>
                    ))}
                  </ul>
                )}
                <Button variant="link" size="sm" className="px-0" onClick={() => setStep(4)}>
                  Change this
                </Button>
              </div>

              <div className="space-y-1">
                <h4 className="font-medium">Exhibit A</h4>
                <p>{cadenceSentence(data)}</p>
                <p className="text-muted-foreground">{initialTransition}</p>
                <Button variant="link" size="sm" className="px-0" onClick={() => setStep(5)}>
                  Change this
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(1, s === 5 && contributionOnly ? 4 : s - 1))}
              disabled={step === 1}
              className="min-h-11"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {step < 6 ? (
              <Button onClick={goNext} className="min-h-11">
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={finish} className="min-h-11">
                Save and prepare the papers
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Where you are</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {STEP_TITLES.map((title, idx) => (
            <Badge key={title} variant={idx + 1 === step ? 'default' : 'outline'}>
              {idx + 1}. {title}
            </Badge>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default CovenantPhase1;
